import React from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormControlLabel,
  Switch,
  Checkbox,
  Divider,
} from '@mui/material';
import vsaasTheme from '../theme';

const panelSx = {
  width: '20%',
  minWidth: 120,
  flexShrink: 0,
  alignSelf: 'stretch',
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  p: 1.25,
  borderRadius: 1,
  bgcolor: 'grey.50',
  border: '1px solid',
  borderColor: 'divider',
  overflow: 'auto',
};

/**
 * Panel lateral fijo junto a la vista previa (ejes, series, mapeo de columnas).
 */
export default function ChartPreviewAxesSeriesPanel({
  chartType,
  columns = [],
  xColumn,
  yColumn,
  groupByColumn,
  labelColumn,
  stacked,
  onXColumnChange,
  onYColumnChange,
  onGroupByColumnChange,
  onLabelColumnChange,
  onStackedChange,
  advancedConfig = {},
  onAdvancedConfigChange,
  legendLabels = [],
  palette,
  onSeriesColorIndexChange,
  heatmapGridConfig,
  onHeatmapGridConfigChange,
  mapConfig,
  onMapConfigChange,
}) {
  const isAxisChart = chartType === 'line' || chartType === 'bar' || chartType === 'scatter';
  const isPieChart = chartType === 'pie' || chartType === 'doughnut';
  const isRadarChart = chartType === 'radar';
  const pal = palette?.colors?.length ? palette : { colors: vsaasTheme.chart.colors };
  const palLen = pal.colors?.length || 1;

  const handleAdvanced = (field, value) => {
    onAdvancedConfigChange?.(field, value);
  };

  const patchHeatmap = (patch) => {
    onHeatmapGridConfigChange?.((prev) => ({ ...prev, ...patch }));
  };

  const patchMap = (patch) => {
    onMapConfigChange?.((prev) => ({ ...prev, ...patch }));
  };

  return (
    <Box sx={panelSx}>
      <Typography variant="caption" fontWeight={700} color="text.secondary">
        Ejes y series del gráfico
      </Typography>

      {chartType === 'table' && (
        <Typography variant="body2" color="text.secondary">
          Columnas visibles, decimales y totales se configuran en la pestaña Datos del Gráfico.
        </Typography>
      )}

      {chartType === 'heatmapGrid' && (
        <>
          <FormControl size="small" fullWidth>
            <InputLabel>Columna de dato (valor)</InputLabel>
            <Select
              label="Columna de dato (valor)"
              value={columns.includes(heatmapGridConfig?.valueColumn || '') ? (heatmapGridConfig?.valueColumn || '') : ''}
              onChange={(e) => patchHeatmap({ valueColumn: e.target.value })}
            >
              {columns.map((col) => (
                <MenuItem key={col} value={col}>
                  {col}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Columna del filtro</InputLabel>
            <Select
              label="Columna del filtro"
              value={columns.includes(heatmapGridConfig?.filterColumn || '') ? (heatmapGridConfig?.filterColumn || '') : ''}
              onChange={(e) => patchHeatmap({ filterColumn: e.target.value })}
            >
              {columns.map((col) => (
                <MenuItem key={col} value={col}>
                  {col}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            fullWidth
            type="number"
            label="Filas de grilla"
            value={heatmapGridConfig?.gridRows ?? ''}
            onChange={(e) => patchHeatmap({ gridRows: e.target.value ? Number(e.target.value) : '' })}
            inputProps={{ min: 1 }}
          />
          <TextField
            size="small"
            fullWidth
            type="number"
            label="Columnas de grilla"
            value={heatmapGridConfig?.gridCols ?? ''}
            onChange={(e) => patchHeatmap({ gridCols: e.target.value ? Number(e.target.value) : '' })}
            inputProps={{ min: 1 }}
          />
        </>
      )}

      {chartType === 'map' && (
        <>
          <FormControl size="small" fullWidth>
            <InputLabel>Columna latitud</InputLabel>
            <Select
              label="Columna latitud"
              value={mapConfig?.latColumn || ''}
              onChange={(e) => patchMap({ latColumn: e.target.value })}
            >
              <MenuItem value="">
                <em>Ninguna</em>
              </MenuItem>
              {columns.map((col) => (
                <MenuItem key={col} value={col}>
                  {col}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Columna longitud</InputLabel>
            <Select
              label="Columna longitud"
              value={mapConfig?.lngColumn || ''}
              onChange={(e) => patchMap({ lngColumn: e.target.value })}
            >
              <MenuItem value="">
                <em>Ninguna</em>
              </MenuItem>
              {columns.map((col) => (
                <MenuItem key={col} value={col}>
                  {col}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Columna dirección</InputLabel>
            <Select
              label="Columna dirección"
              value={mapConfig?.addressColumn || ''}
              onChange={(e) => patchMap({ addressColumn: e.target.value })}
            >
              <MenuItem value="">
                <em>Ninguna</em>
              </MenuItem>
              {columns.map((col) => (
                <MenuItem key={col} value={col}>
                  {col}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </>
      )}

      {(isAxisChart || isPieChart || isRadarChart) && (
        <>
          {(isAxisChart || isRadarChart) && (
            <FormControl size="small" fullWidth>
              <InputLabel>{isRadarChart ? 'Categorías (ejes del radar)' : 'Columna X'}</InputLabel>
              <Select
                label={isRadarChart ? 'Categorías (ejes del radar)' : 'Columna X'}
                value={columns.includes(xColumn) ? xColumn : ''}
                onChange={(e) => onXColumnChange?.(e.target.value)}
              >
                {columns
                  .filter((col) => col !== yColumn)
                  .map((col) => (
                    <MenuItem key={col} value={col}>
                      {col}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          )}

          {isPieChart && (
            <FormControl size="small" fullWidth>
              <InputLabel>Etiquetas (leyenda)</InputLabel>
              <Select
                label="Etiquetas (leyenda)"
                value={columns.includes(xColumn) ? xColumn : ''}
                onChange={(e) => onXColumnChange?.(e.target.value)}
              >
                {columns
                  .filter((col) => col !== yColumn)
                  .map((col) => (
                    <MenuItem key={col} value={col}>
                      {col}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          )}

          <FormControl size="small" fullWidth>
            <InputLabel>{isPieChart ? 'Columna Y (sumar)' : 'Columna Y'}</InputLabel>
            <Select
              label={isPieChart ? 'Columna Y (sumar)' : 'Columna Y'}
              value={columns.filter((col) => col !== xColumn).includes(yColumn) ? yColumn : ''}
              onChange={(e) => onYColumnChange?.(e.target.value)}
            >
              {columns
                .filter((col) => col !== xColumn)
                .map((col) => (
                  <MenuItem key={col} value={col}>
                    {col}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {(isAxisChart || isRadarChart) && (
            <>
              <FormControl size="small" fullWidth>
                <InputLabel>Agrupar por</InputLabel>
                <Select
                  label="Agrupar por"
                  value={groupByColumn === '' || columns.includes(groupByColumn) ? groupByColumn : ''}
                  onChange={(e) => onGroupByColumnChange?.(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Ninguna</em>
                  </MenuItem>
                  {columns
                    .filter((col) => col !== yColumn)
                    .map((col) => (
                      <MenuItem key={col} value={col}>
                        {col}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel>Leyenda (colores)</InputLabel>
                <Select
                  label="Leyenda (colores)"
                  value={labelColumn === '' || columns.includes(labelColumn) ? labelColumn : ''}
                  onChange={(e) => onLabelColumnChange?.(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Ninguna</em>
                  </MenuItem>
                  {columns
                    .filter((col) => col !== yColumn)
                    .map((col) => (
                      <MenuItem key={col} value={col}>
                        {col}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              {chartType === 'bar' && (groupByColumn || labelColumn) && (
                <FormControlLabel
                  control={
                    <Checkbox checked={stacked} onChange={(e) => onStackedChange?.(e.target.checked)} />
                  }
                  label="Barras apiladas"
                />
              )}
            </>
          )}
        </>
      )}

      {isAxisChart && (
        <>
          <Divider sx={{ my: 0.5 }} />
          <Typography variant="caption" fontWeight={600} color="text.secondary">
            Ejes
          </Typography>
          <TextField
            size="small"
            fullWidth
            label="Título eje X"
            value={advancedConfig.xAxisTitle || ''}
            onChange={(e) => handleAdvanced('xAxisTitle', e.target.value)}
            placeholder="Opcional"
          />
          <TextField
            size="small"
            fullWidth
            label="Título eje Y"
            value={advancedConfig.yAxisTitle || ''}
            onChange={(e) => handleAdvanced('yAxisTitle', e.target.value)}
            placeholder="Opcional"
          />
          <FormControlLabel
            control={
              <Switch
                checked={advancedConfig.gridLines !== false}
                onChange={(e) => handleAdvanced('gridLines', e.target.checked)}
              />
            }
            label="Cuadrícula"
          />
          <Typography variant="caption" fontWeight={600} color="text.secondary">
            Límites (opcional)
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
            <TextField
              size="small"
              label="Mín. X"
              type="number"
              value={advancedConfig.xAxisMin ?? ''}
              onChange={(e) => handleAdvanced('xAxisMin', e.target.value)}
              placeholder="Auto"
            />
            <TextField
              size="small"
              label="Máx. X"
              type="number"
              value={advancedConfig.xAxisMax ?? ''}
              onChange={(e) => handleAdvanced('xAxisMax', e.target.value)}
              placeholder="Auto"
            />
            <TextField
              size="small"
              label="Mín. Y"
              type="number"
              value={advancedConfig.yAxisMin ?? ''}
              onChange={(e) => handleAdvanced('yAxisMin', e.target.value)}
              placeholder="Auto"
            />
            <TextField
              size="small"
              label="Máx. Y"
              type="number"
              value={advancedConfig.yAxisMax ?? ''}
              onChange={(e) => handleAdvanced('yAxisMax', e.target.value)}
              placeholder="Auto"
            />
          </Box>
        </>
      )}

      {legendLabels.length > 0 && chartType !== 'table' && chartType !== 'heatmapGrid' && chartType !== 'map' && (
        <>
          <Divider sx={{ my: 0.5 }} />
          <Typography variant="caption" fontWeight={600} color="text.secondary">
            Colores de series
          </Typography>
          {legendLabels.map((lbl, idx) => (
            <Box key={lbl} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <FormControl size="small" sx={{ minWidth: 110, flex: 1 }}>
                <InputLabel shrink>Color</InputLabel>
                <Select
                  label="Color"
                  notched
                  value={(advancedConfig.seriesColorIndex || {})[lbl] ?? idx % palLen}
                  onChange={(e) => onSeriesColorIndexChange?.(lbl, Number(e.target.value))}
                  renderValue={(v) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box
                        sx={{
                          width: 14,
                          height: 14,
                          bgcolor: pal.colors?.[v % palLen] ?? vsaasTheme.primary.main,
                          borderRadius: 0.5,
                          border: '1px solid #ccc',
                        }}
                      />
                      <span>{idx + 1}</span>
                    </Box>
                  )}
                >
                  {Array.from({ length: palLen }, (_, i) => (
                    <MenuItem key={i} value={i}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box
                          sx={{
                            width: 14,
                            height: 14,
                            bgcolor: pal.colors?.[i] ?? vsaasTheme.primary.main,
                            borderRadius: 0.5,
                            border: '1px solid #ccc',
                          }}
                        />
                        Color {i + 1}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" noWrap sx={{ flex: 1 }} title={lbl}>
                {lbl}
              </Typography>
            </Box>
          ))}
        </>
      )}
    </Box>
  );
}
